import React, { useState, useRef, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import { useUserContext } from "../../context/UserContext.tsx";
import POSDropDown from "./POSDropDown.tsx";
import PaymentMethodDropDown from "./PaymentMethodDropDown.tsx";
import SuspiciousDropDown from "./SuspiciousDropDown.tsx";
import { CustomDatePicker } from "./DatePicker/DatePicker.tsx";

const DATE_FORMAT = "MM/dd/yyyy";

export default function OuterFilters({
  setSearchVal,
  filterVal,
  setFilterVal,
  filters,
  setFilters,
  posName,
  setPosName,
  isSuspicious,
  setIsSuspicious,
  paymentMethod,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setPaymentMethod,
  handleFilterChange,
  selectedPOSName,
  setSelectedPOSName,
  initialFilters,
  fetchInitialTransactions,
  fetchFilteredData,
  ResetFilters
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonText, setButtonText] = useState("Date Range");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDateObj, endDateObj] = dateRange;
  const dropdownRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:1200px)");
  const { globalstorevalue } = useUserContext();

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // Helper function to parse date string (YYYY-MM-DD) to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  // Helper function to format date to local YYYY-MM-DD
  const formatDateToLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Sync dateRange state with filters object whenever filters change
  useEffect(() => {
    const startFromFilters = filters.startDate;
    const endFromFilters = filters.endDate;

    if (startFromFilters && endFromFilters) {
      // Both dates exist in filters - restore them
      const startDateObj = parseDate(startFromFilters);
      const endDateObj = parseDate(endFromFilters);

      setDateRange([startDateObj, endDateObj]);
      setButtonText(`${startDateObj.toLocaleDateString()} - ${endDateObj.toLocaleDateString()}`);
    } else if (startFromFilters && !endFromFilters) {
      // Only start date exists
      const startDateObj = parseDate(startFromFilters);
      setDateRange([startDateObj, null]);
      setButtonText(`${startDateObj.toLocaleDateString()} - Select End Date`);
    } else if (!startFromFilters && !endFromFilters) {
      // No dates in filters - reset
      setDateRange([null, null]);
      setButtonText("Date Range");
    }
  }, [filters.startDate, filters.endDate]);

  // Handle date range selection
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    const [start, end] = dates;

    if (start && end) {
      // Both dates selected - format for API and trigger filter
      const formattedStartDate = formatDateToLocal(start);
      const formattedEndDate = formatDateToLocal(end);

      setStartDate(formattedStartDate);
      setEndDate(formattedEndDate);

      // Update button text with both dates
      setButtonText(`${start.toLocaleDateString()} - ${end.toLocaleDateString()}`);

      // Call filter change with date range
      handleFilterChange("dateRange", {
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
    } else if (start && !end) {
      // Only start date selected - show it in button but don't trigger API yet
      setButtonText(`${start.toLocaleDateString()} - Select End Date`);

      // Update start date state but keep end date empty
      const formattedStartDate = formatDateToLocal(start);
      setStartDate(formattedStartDate);
      setEndDate("");

      // Don't trigger API call yet since we don't have end date
    } else if (!start && !end) {
      // Clear dates
      setStartDate("");
      setEndDate("");
      setButtonText("Date Range");

      handleFilterChange("dateRange", {
        startDate: "",
        endDate: ""
      });
    }
  };

  // Only reset when store changes AND no dates are in filters
  // This prevents resetting when dates are preserved after store change
  useEffect(() => {
    // Only reset if there are no dates in filters
    if (!filters.startDate && !filters.endDate) {
      setDateRange([null, null]);
      setButtonText("Date Range");
    }
  }, [globalstorevalue]);

  return (
    <div className="flex gap-2 flex-wrap items-center">
      <div className={`filterPOSDropDown ${filters.posId ? "active" : ""}`}>
        <POSDropDown
          selectedPOSName={selectedPOSName}
          setSelectedPOSName={setSelectedPOSName}
          filterVal={filterVal}
          posId={filters.posId}
          setPosId={(value) => handleFilterChange("posId", value)}
          filters={filters}
        />
      </div>

      <div
        className={`filterPaymentMethodDropDown ${filters.paymentMethod && filters.paymentMethod !== "All Methods" ? "active" : ""
          }`}
      >
        <PaymentMethodDropDown
          paymentMethod={filters.paymentMethod}
          setPaymentMethod={(value) =>
            handleFilterChange("paymentMethod", value)
          }
          filters={filters}
        />
      </div>

      <div
        className={`filterSuspiciousDropDown ${filters.isSuspicious !== "" ? "active" : ""
          }`}
      >
        <SuspiciousDropDown
          isSuspicious={filters.isSuspicious}
          setIsSuspicious={(value) => handleFilterChange("isSuspicious", value)}
          filters={filters}
        />
      </div>

      {/* Date Range Picker */}
      <div className={`filterDateRangeDropDown ${filters.startDate && filters.endDate ? "active" : ""}`}>
        <CustomDatePicker
          selected={startDateObj}
          startDate={startDateObj}
          endDate={endDateObj}
          selectsRange={true}
          handleChangeRange={handleDateRangeChange}
          leftLabel={buttonText}
          maxDate={new Date()}
          className={`date-range-picker-button ${filters.startDate && filters.endDate ? "active" : ""}`}
        />
      </div>

      {/* Reset Filters Button */}
      {filterVal && (
        <button
          onClick={ResetFilters}
          className="px-3 py-1.5 text-[12px] font-medium bg-gold-2 hover:bg-gold-3 text-white border-none rounded-md cursor-pointer transition-colors"
        >
          Reset Filters
        </button>
      )}
    </div>
  );
}
